import {Component, Input, OnInit} from '@angular/core';
import {Website} from "../../../website";
import * as fromStore from "../../../../../store";
import {Store} from "@ngrx/store";
import {filter, first, switchMap} from "rxjs/operators";
import {combineLatest} from "rxjs";
import {FormControl, FormGroup} from "@angular/forms";
import {EcommerceService} from "../../ecommerce.service";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";

@Component({
    selector: 'db-ecommerce-emails-settings',
    templateUrl: './emails-settings.component.html',
    styleUrls: ['./emails-settings.component.scss'],
    standalone: false
})
export class EmailsSettingsComponent implements OnInit {
  @Input() website: Website;
  public loading: boolean;
  public disabledBtn: boolean = true;
  public primaryColor: string = '#ffffff';
  public bgColor: string = '#ffffff';
  public bodyBgColor: string = '#ffffff';
  public textColor: string = '#ffffff';


  public emailsSettingsForm: FormGroup = new FormGroup({
    woocommerce_email_from_name: new FormControl(''),
    woocommerce_email_from_address: new FormControl(''),
    woocommerce_email_footer_text: new FormControl(''),
    woocommerce_email_base_color: new FormControl(''),
    woocommerce_email_background_color: new FormControl(''),
    woocommerce_email_body_background_color: new FormControl(''),
    woocommerce_email_text_color: new FormControl(''),
  });

  public settings: any;

  constructor(
    private _ecommerceService: EcommerceService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private readonly _store: Store<fromStore.DbStoreState>,
  ) { }

  ngOnInit() {
    this._formGroupValueChanges();
    this._getEmailsSettings();
  }

  private _getEmailsSettings(): void {

    this._store.select(fromStore.getEcommerceEmailsSettingsLoaded(this.website?.id))
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getEcommerceEmailsSettingsFromName(this.website?.id)),
          this._store.select(fromStore.getEcommerceEmailsSettingsFromAddress(this.website?.id)),
          this._store.select(fromStore.getEcommerceEmailsSettingsFooterText(this.website?.id)),
          this._store.select(fromStore.getEcommerceEmailsSettingsPrimaryColor(this.website?.id)),
          this._store.select(fromStore.getEcommerceEmailsSettingsBgColor(this.website?.id)),
          this._store.select(fromStore.getEcommerceEmailsSettingsBodyBgColor(this.website?.id)),
          this._store.select(fromStore.getEcommerceEmailsSettingsTextColor(this.website?.id)),
        ))
      )
      .subscribe(([fromName, fromAddress, footerText, primaryColor, bgColor, bodyBgColor, textColor]) => {
        this.settings = {
          'fromName': fromName,
          'fromAddress': fromAddress,
          'footerText': footerText,
          'primaryColor': primaryColor,
          'bgColor': bgColor,
          'bodyBgColor': bodyBgColor,
          'textColor': textColor
        }
        this.primaryColor = this.settings?.primaryColor?.value;
        this.bgColor = this.settings?.bgColor?.value;
        this.bodyBgColor = this.settings?.bodyBgColor?.value;
        this.textColor = this.settings?.textColor?.value;
        this._addValuesToForm();
      });
  }

  private _addValuesToForm(): void {
    // Define a mapping between settings keys and form control names
    const settingsToFormControlMap = {
      fromName: 'woocommerce_email_from_name',
      fromAddress: 'woocommerce_email_from_address',
      footerText: 'woocommerce_email_footer_text',
      primaryColor: 'woocommerce_email_base_color',
      bgColor: 'woocommerce_email_background_color',
      bodyBgColor: 'woocommerce_email_body_background_color',
      textColor: 'woocommerce_email_text_color'
    };

// Iterate over the settings and apply values to the form controls
    Object.keys(this.settings).forEach(key => {
      const formControlName = settingsToFormControlMap[key];
      if (this.emailsSettingsForm.controls[formControlName]) {
        this.emailsSettingsForm.controls[formControlName].setValue(this.settings[key].value);
      }
    });
  }

  private _formGroupValueChanges(): void {
    this.emailsSettingsForm && this.emailsSettingsForm.valueChanges.subscribe(() => {
      this.disabledBtn = false;
    });
  }

  public saveSettings(): void {
    this.loading = true;
    const params = [
      {
        "id": "woocommerce_email_from_name",
        "value": this.emailsSettingsForm?.controls['woocommerce_email_from_name'].value
      },
      {
        "id": "woocommerce_email_from_address",
        "value": this.emailsSettingsForm?.controls['woocommerce_email_from_address'].value
      },
      {
        "id": "woocommerce_email_footer_text",
        "value": this.emailsSettingsForm?.controls['woocommerce_email_footer_text'].value
      },
      {
        "id": "woocommerce_email_base_color",
        "value": this.primaryColor
      },
      {
        "id": "woocommerce_email_background_color",
        "value": this.bgColor
      },
      {
        "id": "woocommerce_email_body_background_color",
        "value": this.bodyBgColor
      },
      {
        "id": "woocommerce_email_text_color",
        "value": this.textColor
      }
    ];
    this._ecommerceService.updateEmailsSettings(this.website?.id, params)
      .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        if (response.success && response?.data) {
          this._store.dispatch(new fromStore.LoadEcommerceEmailsSettings({websiteId: this.website?.id}));
          setTimeout(() => {
            this.loading = false;
          } , 1000);
        }
      });
  }

  private _sendEvent(): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Ecommerce - Email Templates',
      eventLabel: 'Save'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }
}
